import React, { useCallback, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Stack } from 'new/elements/Stack';
import { useAppContext } from 'new/hooks/useAppContext';
import { getResourceEditModalOpen } from 'js/reducers/app/selectors';
import { POLLING_INTERVAL_MS } from 'js/constants/config';
import useEntities from 'new/state/entities/repository/useEntities';
import SourcePipeline from 'js/components/pipelines/SourcePipelineContainer';
import { ConnectionLoader } from 'new/elements/ConnectionLoader/ConnectionLoader';
import usePipelinesAdapter from 'new/state/pipelines/adapter/usePipelinesAdapter';
import { DraftConfirmationDialog } from '../genai_rag/components/DraftConfirmationDialog';
import { DraftContextProvider } from 'new/components/flows/types/genai_rag/context/Draft/DraftContextProvider';
import useMetricsAdapter from 'new/state/metrics/adapter/useMetricsAdapter';
import { getFlowMetricsInterval } from 'js/reducers/metrics/selectors';
import { EditFlowName } from 'new/components/flows/EditFlowName/EditFlowName';
import FlowDetailViewContainer from 'js/components/data_flows/cards/FlowDetailViewContainer';
import styles from './EditBaseFlow.module.scss';

export const EditBaseFlowEmbedded = () => {
  const { setCurrentSlug } = useAppContext();
  const { refreshPipelineById } = usePipelinesAdapter();
  const { requestFlowMetrics } = useMetricsAdapter();
  const { id: rootNodeId, type: flowType } = useParams<any>();
  const onRootRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const hasLoadedRef = useRef(false);
  const prevRootNodeIdRef = useRef(rootNodeId);
  const flowMetricsInterval = useSelector(getFlowMetricsInterval);
  const resourceEditModalOpen = useSelector(getResourceEditModalOpen);
  const selectedCardID = useSelector((state: any) => state.pipelines?.detailView?.nodeKey);

  const { data: entities } = useEntities();
  const flow = entities.flowNodes[rootNodeId];
  const source = entities.sources[flow?.data_source_id];

  if (prevRootNodeIdRef.current !== rootNodeId) {
    hasLoadedRef.current = false;
    prevRootNodeIdRef.current = rootNodeId;
  }

  if (source && flowType) {
    hasLoadedRef.current = true;
  }

  // Keep refreshPipeline's identity STABLE and read the interval at call-time via
  // a ref. SourcePipeline's memo comparator ignores the refreshPipeline prop, so a
  // changing identity never reaches the toolbar refresh button — it would stay
  // bound to the first closure (default interval = latest run) and ignore the
  // toggle. This mirrors the list view (stable bound handler reading current
  // state) and EditBaseFlow. See NEX-21823.
  const flowMetricsIntervalRef = useRef(flowMetricsInterval);
  flowMetricsIntervalRef.current = flowMetricsInterval;

  const refreshPipeline = useCallback(() => {
    const sourceId = flow?.data_source_id ?? null;
    refreshPipelineById(rootNodeId);
    requestFlowMetrics(rootNodeId, sourceId, { interval: flowMetricsIntervalRef.current });
  }, [rootNodeId, flow?.data_source_id, requestFlowMetrics]);

  // Initialize pipeline on mount and set up auto-refresh timer
  useEffect(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    if (!rootNodeId || resourceEditModalOpen) {
      return;
    }

    refreshPipeline();
    timerRef.current = setInterval(refreshPipeline, POLLING_INTERVAL_MS);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [rootNodeId, resourceEditModalOpen, refreshPipeline]);

  useEffect(() => {
    setCurrentSlug(rootNodeId);
  }, [rootNodeId, flow, setCurrentSlug]);

  if ((!source || !flowType) && !hasLoadedRef.current) {
    return <ConnectionLoader loading message="Preparing the Flow" />;
  }

  if (!source || !flowType) {
    return null;
  }

  return (
    <DraftContextProvider flowId={rootNodeId}>
      <Stack ref={onRootRef} flex={1} height="100%" position="relative">
        <EditFlowName flowId={rootNodeId} embedded />
        <div className={styles.sourcePipelineContainer}>
          <SourcePipeline
            refreshPipeline={refreshPipeline}
            onRootRef={onRootRef}
            dataSource={source}
            fullScreen
          />
        </div>
      </Stack>
      {selectedCardID && <FlowDetailViewContainer />}
      <DraftConfirmationDialog />
    </DraftContextProvider>
  );
};
